/**
 * Centralized data normalization utilities for handling legacy data and ensuring consistent data shapes
 */

import { Timestamp } from 'firebase/firestore';
import { GameType } from '../types/game-engine.types';

/**
 * Normalize date fields from various Firestore formats to standard Date objects
 */
export function normalizeDate(dateField: unknown): Date {
  if (dateField instanceof Date) {
    return dateField;
  }
  
  // Firestore Timestamp object
  if (dateField && typeof dateField === 'object' && 'toDate' in dateField && typeof (dateField as Timestamp).toDate === 'function') {
    return (dateField as Timestamp).toDate();
  }
  
  // Firestore Timestamp as plain object
  if (dateField && typeof dateField === 'object' && 'seconds' in dateField && typeof (dateField as { seconds: number }).seconds === 'number') {
    return new Date((dateField as { seconds: number }).seconds * 1000);
  }
  
  // ISO string
  if (typeof dateField === 'string') {
    const parsed = new Date(dateField);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }
  
  // Fallback for corrupted/invalid dates
  console.warn('🔧 Invalid date field detected, using current date:', dateField);
  return new Date();
}

/**
 * Normalize game type
 * Now accepts any registered plugin type (wheel, scratchcard, game-board, etc.)
 */
export function normalizeGameType(gameType: string | undefined): GameType {
  // Accept any non-empty string as valid game type (plugin-based architecture)
  if (gameType && typeof gameType === 'string' && gameType.trim().length > 0) {
    return gameType;
  }
  // Only warn and default if truly invalid
  console.warn('🔧 Invalid game type, defaulting to wheel:', gameType);
  return 'wheel';
}

/**
 * Game type display utilities - centralized to avoid duplication
 * NOTE: For new code, prefer using GamePluginRegistry.getPlugin() for dynamic lookup.
 * This config is maintained for backward compatibility with data normalization.
 */
export const GAME_TYPE_CONFIG = {
  wheel: {
    display: 'Spin Wheel',
    icon: 'donut_large',
    description: 'Interactive spinning wheel game'
  },
  scratchcard: {
    display: 'Scratch Card',
    icon: 'touch_app',
    description: 'Digital scratch-off lottery cards'
  },
  'game-board': {
    display: 'Game Board',
    icon: 'grid_4x4',
    description: 'Monopoly-style board game with dice'
  },
  mixed: {
    display: 'Mixed Games',
    icon: 'casino',
    description: 'Campaign with multiple game types'
  }
} as const;

export function getGameTypeDisplay(gameType: GameType | string): string {
  const config = GAME_TYPE_CONFIG[gameType as keyof typeof GAME_TYPE_CONFIG];
  return config?.display || gameType || 'Unknown';
}

export function getGameTypeIcon(gameType: GameType | string): string {
  const config = GAME_TYPE_CONFIG[gameType as keyof typeof GAME_TYPE_CONFIG];
  return config?.icon || 'games';
}

/**
 * Normalize array fields with proper defaults
 */
export function normalizeArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Normalize object fields with proper defaults
 */
export function normalizeObject<T extends Record<string, unknown>>(value: T | undefined | null, defaultValue: T): T {
  return value && typeof value === 'object' ? { ...defaultValue, ...value } : defaultValue;
}

/**
 * Deep clean an object, removing undefined values and normalizing nested data
 */
export function deepCleanObject<T extends Record<string, unknown>>(obj: T): T {
  const cleaned = {} as T;
  
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        cleaned[key as keyof T] = normalizeArray(value) as T[keyof T];
      } else if (value instanceof Date || (typeof value === 'object' && 'toDate' in value)) {
        cleaned[key as keyof T] = normalizeDate(value) as T[keyof T];
      } else if (typeof value === 'object') {
        cleaned[key as keyof T] = deepCleanObject(value as Record<string, unknown>) as T[keyof T];
      } else {
        cleaned[key as keyof T] = value as T[keyof T];
      }
    }
  }
  
  return cleaned;
}
